import React, { useState, useEffect, useCallback } from 'react';
import { Box, Image, Flex, Spinner } from '@chakra-ui/react';

import sigliveCuriosity from 'Asset/image/siglive-curiosity.svg';
import { Condition, Iteration } from 'Component/others';
import { TextBold, TextRegular } from 'UiKit/typography';

import styles from './styles.module.css';
import { ChevronLeftIcon, ChevronRightIcon } from 'Asset/icon';

const PreviewImage = ({ images, isOpen, onClose, initialIndex = 0 }) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isMainImageLoading, setIsMainImageLoading] = useState(true);
  const [failedImages, setFailedImages] = useState(new Set());
  const [loadingThumbnails, setLoadingThumbnails] = useState(new Set());
  const [thumbnailErrors, setThumbnailErrors] = useState(new Set());

  const handleNextImage = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % images.length);
  }, [images]);

  const handlePreviousImage = useCallback(() => {
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images]);

  useEffect(() => {
    setSelectedIndex(initialIndex);
  }, [initialIndex, isOpen]);

  useEffect(() => {
    setIsMainImageLoading(Boolean(images?.[selectedIndex]?.url));
    // Only reset loading state, don't reset failed images
  }, [selectedIndex, images]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsMainImageLoading(true);
        setFailedImages(new Set());
        setLoadingThumbnails(new Set());
        setThumbnailErrors(new Set());
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNextImage();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePreviousImage();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, images, handleNextImage, handlePreviousImage]);

  useEffect(() => {
    if (images && images.length > 1) {
      setLoadingThumbnails(new Set(images.filter((image) => image?.url).map((_, index) => index)));
      setThumbnailErrors(new Set());
    }
  }, [images]);

  if (!shouldRender || !images || images.length === 0) {
    return null;
  }

  const handleThumbnailClick = (index) => {
    setSelectedIndex(index);
  };

  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Handles click event on the backdrop of the modal.
   * @param {React.MouseEvent} e The event object.
   * @private
   */
  /*******  ae261520-c4cf-40eb-b89b-4171222b4152  *******/
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleMainImageLoad = () => {
    setIsMainImageLoading(false);
  };

  const handleMainImageError = () => {
    setIsMainImageLoading(false);
    setFailedImages((prev) => {
      const newSet = new Set(prev);
      newSet.add(selectedIndex);
      return newSet;
    });
  };

  const handleThumbnailLoad = (index) => {
    setLoadingThumbnails((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    // Don't remove from thumbnailErrors - once failed, stay failed
  };

  const handleThumbnailError = (index) => {
    setLoadingThumbnails((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    setThumbnailErrors((prev) => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
  };

  const currentImageHasFailed = failedImages.has(selectedIndex);

  return (
    <Box data-testid="preview-image__container" className={`${styles['preview-overlay']} ${isVisible ? styles['preview-overlay--visible'] : ''}`}>
      <Box className={styles['preview-container']} onClick={handleBackdropClick}>
        <Box className={styles['main-image-container']} onClick={handleBackdropClick}>
          <Condition statement={isMainImageLoading && !currentImageHasFailed}>
            <Box className={styles['loading-indicator']}>
              <Spinner size="xl" color="white" thickness="3px" />
            </Box>
          </Condition>

          <Condition
            statement={!currentImageHasFailed && images[selectedIndex]?.url}
            fallback={
              <Box className={styles['main-image__container--not-found']}>
                <Box className={styles['main-image__box--not-found']}>
                  <Box className={styles['not-found__image--wrapper']}>
                    <Image src={sigliveCuriosity} alt="Image not found" />
                  </Box>
                  <Flex justifyContent={'center'} alignItems={'center'} flexDirection={'column'} gap={'4px'}>
                    <TextRegular className={styles['not-found__text']}>Image not found</TextRegular>
                    <TextRegular className={styles['not-found__text']}>Ada masalah ketika membuka gambar</TextRegular>
                  </Flex>
                </Box>
              </Box>
            }
          >
            <Box className={styles['image-caption__container']}>
              <Image
                src={images[selectedIndex]?.url}
                alt={`Preview ${selectedIndex + 1}`}
                className={styles['main-image']}
                objectFit="contain"
                onLoad={handleMainImageLoad}
                onError={handleMainImageError}
              />
              <Condition statement={images[selectedIndex]?.caption}>
                <Box className={styles['caption__container']}>
                  <TextBold className={styles['caption']}>{images[selectedIndex]?.caption}</TextBold>
                </Box>
              </Condition>
            </Box>
          </Condition>
        </Box>

        <Condition statement={images.length > 1}>
          <Flex className={styles['thumbnail-container']}>
            <Box className={styles['thumbnail-navigation']} onClick={handlePreviousImage}>
              <ChevronLeftIcon />
            </Box>
            <Iteration
              items={images}
              render={(image, index) => {
                const thumbnailHasError = thumbnailErrors.has(index) || !image?.url;
                return (
                  <Box
                    key={index}
                    className={`
                    ${styles['thumbnail-wrapper']}
                    ${selectedIndex === index ? styles['thumbnail-wrapper--active'] : ''}
                    ${thumbnailErrors.has(index) ? styles['thumbnail-wrapper--error'] : ''}
                  `}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <Condition statement={loadingThumbnails.has(index) && !thumbnailErrors.has(index)}>
                      <Box className={styles['thumbnail-loading']}>
                        <Spinner size="sm" color="white" />
                      </Box>
                    </Condition>
                    <Image
                      src={thumbnailHasError ? sigliveCuriosity : image?.url}
                      alt={thumbnailHasError ? 'Image not available' : `Thumbnail ${index + 1}`}
                      className={styles['thumbnail-image']}
                      objectFit="cover"
                      onLoad={() => handleThumbnailLoad(index)}
                      onError={() => handleThumbnailError(index)}
                      style={{ opacity: loadingThumbnails.has(index) ? 0 : 1 }}
                    />
                  </Box>
                );
              }}
            />
            <Box className={styles['thumbnail-navigation']} onClick={handleNextImage}>
              <ChevronRightIcon />
            </Box>
          </Flex>
        </Condition>
      </Box>
    </Box>
  );
};

export default PreviewImage;
